/**
 * Grid Targeting Utility
 * @date_of_change 2025-09-16 - Shared smart grid targeting for AoE and measurement systems
 *
 * Provides intelligent grid targeting that can snap to either grid centers or grid intersections
 * based on mouse proximity. Supports D&D 5e precision requirements for AoE and measurement.
 */

/**
 * Calculate smart grid targeting position
 * @param {number} exactX - Exact X position in grid units (fractional)
 * @param {number} exactY - Exact Y position in grid units (fractional)
 * @param {number} intersectionThreshold - Distance threshold for snapping to intersections (0.0-0.5)
 * @returns {Object} - {x, y, targetMode, exact, fractional}
 */
export const calculateGridTarget = (exactX, exactY, intersectionThreshold = 0.2) => {
  // Determine fractional position within grid cell
  const fractionalX = exactX - Math.floor(exactX);
  const fractionalY = exactY - Math.floor(exactY);

  let targetX, targetY, targetMode;

  // Check X coordinate targeting
  if (fractionalX <= intersectionThreshold) {
    // Snap to left intersection
    targetX = Math.floor(exactX);
    targetMode = 'intersection';
  } else if (fractionalX >= (1 - intersectionThreshold)) {
    // Snap to right intersection
    targetX = Math.ceil(exactX);
    targetMode = 'intersection';
  } else {
    // Snap to center
    targetX = Math.floor(exactX);
    targetMode = 'center';
  }

  // Check Y coordinate targeting
  if (fractionalY <= intersectionThreshold) {
    // Snap to top intersection
    targetY = Math.floor(exactY);
    targetMode = 'intersection';
  } else if (fractionalY >= (1 - intersectionThreshold)) {
    // Snap to bottom intersection
    targetY = Math.ceil(exactY);
    targetMode = 'intersection';
  } else {
    // Snap to center (only if X was also center)
    targetY = Math.floor(exactY);
    if (targetMode !== 'intersection') {
      targetMode = 'center';
    }
  }

  return {
    x: targetX,
    y: targetY,
    targetMode: targetMode,
    exact: { x: exactX, y: exactY },
    fractional: { x: fractionalX, y: fractionalY }
  };
};

/**
 * Convert pixel coordinates to smart grid coordinates
 * @param {number} pixelX - X position in pixels
 * @param {number} pixelY - Y position in pixels
 * @param {number} gridSize - Size of grid cells in pixels
 * @param {number} intersectionThreshold - Distance threshold for snapping to intersections
 * @returns {Object} - Grid targeting information
 */
export const pixelsToGridTarget = (pixelX, pixelY, gridSize, intersectionThreshold = 0.2) => {
  const exactX = pixelX / gridSize;
  const exactY = pixelY / gridSize;

  return calculateGridTarget(exactX, exactY, intersectionThreshold);
};

/**
 * Convert grid coordinates back to pixel coordinates
 * @param {Object} gridTarget - Grid target from calculateGridTarget
 * @param {number} gridSize - Size of grid cells in pixels
 * @returns {Object} - {x, y} pixel coordinates
 */
export const gridTargetToPixels = (gridTarget, gridSize) => {
  let pixelX, pixelY;

  if (gridTarget.targetMode === 'center') {
    // Center of grid cell
    pixelX = gridTarget.x * gridSize + (gridSize / 2);
    pixelY = gridTarget.y * gridSize + (gridSize / 2);
  } else {
    // Grid intersection (corner)
    pixelX = gridTarget.x * gridSize;
    pixelY = gridTarget.y * gridSize;
  }

  return { x: pixelX, y: pixelY };
};

/**
 * Create visual indicator for targeting mode
 * @param {Object} gridTarget - Grid target from calculateGridTarget
 * @param {number} gridSize - Size of grid cells in pixels
 * @returns {Object} - Visual indicator properties {color, radius, showCross}
 */
export const getTargetingVisuals = (gridTarget) => {
  if (gridTarget.targetMode === 'intersection') {
    return {
      color: 'orange',
      radius: 6,
      showCross: true,
      crossSize: 8
    };
  } else {
    return {
      color: 'red',
      radius: 4,
      showCross: false,
      crossSize: 0
    };
  }
};